import { Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import style from '../jobs.modules.scss'
import { Table } from 'components/core-ui/Table'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useJobCategoriesQuery, useJobsQuery } from 'components/iam/jobs/api'
import { rowActions } from '../helpers'
import { columns, initialFilter, toolbarActions, updateFilter } from './helpers'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import EditJobModal from './EditJob/EditJobModal'
import { DeleteModal } from './components/DeleteModal'
import { Autocomplete } from '@fullscreendigital/carrefour-dex-fe'

const Jobs: React.FC = () => {
    const [modalState, setModalState] = useState({
        isEditModalOpen: false,
        isDisableModalOpen: false,
        selectedRowId: null
    });
    const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
    const { canCreate, canEdit, canDelete } = { canCreate: true, canEdit: true, canDelete: false }
    const [isLoading] = useState<boolean>(true)
    const [searchParams] = useSearchParams()
    const [searchJCText, setSearchJCText] = useState<string>('')
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>('')
    const { showNotification } = useNotification()


    const undefinedJobsParam = searchParams.get('undefinedJobs')
    const isUndefinedJobs = undefinedJobsParam === 'true'

    const { data: jobCategoriesData, isLoading: isLoadingJobCat } = useJobCategoriesQuery(
        showNotification,
        `/iam/management-tools/job-categories?searchBy=name&searchValue=${debouncedSearchText}`
    );

    const navigate = useNavigate()

    const openEditModal = useCallback((jobId?: number) => {
        setModalState({ ...modalState, isEditModalOpen: true, selectedRowId: jobId })
    }, [modalState])

    const closeEditModal = useCallback(() => {
        setModalState({ ...modalState, isEditModalOpen: false, selectedRowId: null })
        refetch()
    }, [modalState])

    const openDisableModal = useCallback((jobId: number) => {
        setModalState({ ...modalState, isDisableModalOpen: true, selectedRowId: jobId })
    }, [modalState])

    const closeDisableModal = useCallback(() => {
        setModalState({ ...modalState, isDisableModalOpen: false, selectedRowId: null })
        refetch()
    }, [modalState])

    const selectRow = useCallback((rowId: number, action: string) => {
        action === 'edit' ? openEditModal(rowId) : openDisableModal(rowId)
    }, [openEditModal, openDisableModal])




    const jobCategoryOptions = useMemo(() => {
        return jobCategoriesData?.list.map(category => ({
            value: category.name,
            label: category.name,
        })) || [];
    }, [jobCategoriesData])
    const handleJobCatSearchChange = useCallback((event, value) => {
        setSearchJCText(value)
    }, [])

    const drawerFilters = [
        {
            name: 'jobCategory.name',
            value: undefined,
            filter: (
                <Autocomplete
                    variant="outlined"
                    label="Job Category"
                    options={jobCategoryOptions}
                    loading={isLoadingJobCat}
                    onInputChange={handleJobCatSearchChange}
                />
            ),
            type: 'autocomplete',
            label: 'JobCategory'
        },
        {
            name: 'active',
            value: undefined,
            filter: (
                <Autocomplete
                    variant="outlined"
                    label="active"
                    disableClearable
                    options={[
                        { value: 'true', label: 'Active' },
                        { value: 'false', label: 'Inactive' }
                    ]}
                />
            ),
            type: 'autocomplete',
            label: 'Status'
        }
    ]

    const [fetchParams, setFetchParams] = useState(initialFilter)
    const { data: jobsData, isLoading: fetchLoading, refetch } = useJobsQuery(showNotification, toTableGETUrl({
        ...fetchParams,
        url: '/iam/management-tools/jobs',
        searchFields: ['jobTitle'],
        tableFilters: isUndefinedJobs ? [{ name: 'jobCategory', value: 'null' }] : fetchParams.tableFilters,
    }))

    const handleRemoteData = useCallback(({ page, rowsPerPage, order, orderBy, filters, tableFilters, searchText }) => {
        const updatedOrderBy = orderBy === 'jobCategory' ? 'jobCategory.id' : orderBy
        setFetchParams({ page, rowsPerPage, order, orderBy: updatedOrderBy, filters, tableFilters, searchText })
    }, [])

    useEffect(() => {
        setFetchParams(prevFetchParams => {
            const updatedFilters = [...(prevFetchParams.tableFilters || [])]
    
            if (isUndefinedJobs) {
                updateFilter(updatedFilters, 'jobCategory', 'null')
                updateFilter(updatedFilters, 'jobCategory.name', undefined)
            } else {
                const jobUndefinedFilterIndex = updatedFilters.findIndex(filter => filter.name === 'jobCategory')
                if (jobUndefinedFilterIndex !== -1) {
                    updatedFilters[jobUndefinedFilterIndex].value = undefined
                }
            }
    
            return { ...prevFetchParams, tableFilters: updatedFilters }
        });
    }, [searchParams, isUndefinedJobs])

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchJCText)
        }, 300);

        return () => {
            clearTimeout(handler)
        };
    }, [searchJCText])

    const handleSuccess = () => {
        setShouldRefreshData(true)
    }

    const toolbar = useMemo(() => toolbarActions({ canCreate, navigate, openModal: openEditModal, isUndefinedJobs }), [canCreate, navigate, openEditModal, isUndefinedJobs])

    return (
        <div className={style.mainContainer}>
            {modalState.isEditModalOpen && (
                <EditJobModal
                    isModalOpen={modalState.isEditModalOpen}
                    onModalClose={closeEditModal}
                    jobId={modalState.selectedRowId}
                    onSuccess={handleSuccess}
                />
            )}
            {canDelete && (
                <DeleteModal
                    isModalOpen={modalState.isDisableModalOpen}
                    closeModal={closeDisableModal}
                    itemId={modalState.selectedRowId}
                    setShouldRefreshData={setShouldRefreshData}
                />
            )}
            <Box>
                <Table
                    hideCheckbox
                    hasSearchInput
                    orderBy={fetchParams.orderBy}
                    order={fetchParams.order}
                    searchText={fetchParams.searchText}
                    columns={columns}
                    items={jobsData?.list || []}
                    isLoading={isLoading && fetchLoading}
                    totalCount={jobsData?.page.totalCount || fetchParams.page}
                    shouldRefreshData={shouldRefreshData}
                    setShouldRefreshData={setShouldRefreshData}
                    rowsPerPage={fetchParams.rowsPerPage}
                    rowActionsWidth="130px"
                    fetchData={handleRemoteData}
                    rowActions={rowActions({ canEdit, canDelete, navigate, selectRow, detailsUrl: '/iam/jobManagement/job' })}
                    filters={drawerFilters}
                    toolbarActions={toolbar}
                />
            </Box>
        </div>
    )
}

export default Jobs